import { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import request from '../helpers/request';

/**
 * Custom hook for managing API keys state and operations
 * @returns {object} API keys state and operations
 */
export const useApiKeys = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch all API keys
  const fetchApiKeys = useCallback(async () => {
    setLoading(true);
    try {
      const response = await request.get('/api/v1/api-keys/');
      setApiKeys(response.data);
    } catch (error) {
      console.error('Failed to fetch API keys', error);
      toast.error('Failed to load API keys.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new API key
  const createApiKey = useCallback(async (name, description) => {
    if (!name.trim()) {
      toast.error('API Key name is required!');
      return null;
    }
    try {
      const response = await request.post('/api/v1/api-keys/', { name, description });
      toast.success('API Key created successfully!');
      await fetchApiKeys();
      return response.data;
    } catch (error) {
      console.error('Error creating API key:', error);
      toast.error('Failed to create API key.');
      return null;
    }
  }, [fetchApiKeys]);

  // Delete an API key
  const deleteApiKey = useCallback(async (id) => {
    try {
      await request.delete(`/api/v1/api-keys/${id}/`);
      toast.warn('API Key deleted!');
      await fetchApiKeys();
      return true;
    } catch (error) {
      console.error('Error deleting API key:', error);
      toast.error('Failed to delete API key.');
      return false;
    }
  }, [fetchApiKeys]);

  // Initial fetch on mount
  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);

  return {
    apiKeys,
    loading,
    fetchApiKeys,
    createApiKey,
    deleteApiKey,
  };
};

export default useApiKeys;
